import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { Card, Button, inputClass } from './dashboardShared';

// The dashboard's Website tab: the banner across the top of cheeks.uk, my status bubble, the live indicator and the
// Instagram follower count. What's showing now is read straight from the site's data; changes go through the bot's
// own commands (?banner, ?mystatus, ?wn live / offline, ?membercount instagram), which still work in Discord too.

const TEXTS = [
  { what: 'banner', key: 'bannerText', title: 'Site banner', placeholder: 'Announcement across the top of the site',
    help: 'Links work like this: [Link name](https://example.com). Clear it to hide the banner.', clear: 'Hide banner' },
  { what: 'status', key: 'myStatus', title: 'My status', placeholder: 'e.g. Scarlet & Violet boosters in stock!',
    help: 'The bubble on your profile. Clear it to go back to the default.', clear: 'Clear status' },
  { what: 'instagram', key: 'instagramCount', title: 'Instagram followers', placeholder: 'e.g. 12,400',
    help: "Shown on the site's stats. Whatnot and Discord numbers update by themselves." },
];

export default function WebsiteTab({ ask, database, setNote, failed }) {
  const [current, setCurrent] = useState({});  // what the site shows now
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');

  useEffect(() => {
    const offs = ['bannerText', 'myStatus', 'instagramCount', 'isLive'].map((key) =>
      onValue(ref(database, key), (snap) => setCurrent((c) => ({ ...c, [key]: snap.val() })), () => {}));
    return () => offs.forEach((off) => off());
  }, [database]);

  const send = async (what, value, label) => {
    setBusy(label); setNote(null);
    const answer = await ask('site.set', { what, value });
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: answer.data.message, ok: answer.data.ok });
    if (answer.data.ok) setDrafts((d) => ({ ...d, [what]: undefined }));
  };

  const live = current.isLive === true;
  return (
    <div className="space-y-4">
      <Card title="Live indicator" right={
        <Button small tone={live ? 'danger' : 'primary'} onClick={() => send('live', !live, 'live')} disabled={!!busy || current.isLive === undefined}>
          {busy === 'live' ? '…' : live ? 'End live' : '📡 Go live'}
        </Button>
      }>
        <p className="text-xs flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${live ? 'bg-red-500' : 'bg-zinc-500'}`} />
          <span className="text-zinc-300">cheeks.uk is showing <b className="text-white">{current.isLive === undefined ? '…' : live ? 'LIVE' : 'offline'}</b></span>
        </p>
        <p className="text-zinc-500 text-[11px] mt-1">It also switches by itself when you go live or end on Whatnot.</p>
      </Card>

      {TEXTS.map(({ what, key, title, placeholder, help, clear }) => {
        const shown = current[key] == null ? '' : String(current[key]);
        const draft = drafts[what] ?? shown;
        return (
          <Card key={what} title={title}>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={draft} onChange={e => setDrafts(d => ({ ...d, [what]: e.target.value }))} placeholder={placeholder}
                onKeyDown={e => { if (e.key === 'Enter' && draft.trim() && draft !== shown && !busy) send(what, draft, what); }}
                className={`flex-1 ${inputClass}`} />
              <div className="flex gap-1.5">
                <Button tone="primary" onClick={() => send(what, draft, what)} disabled={!!busy || !draft.trim() || draft === shown}>
                  {busy === what ? 'Saving…' : 'Save'}
                </Button>
                {clear && (
                  <Button tone="ghost" onClick={() => send(what, '', `${what}-clear`)} disabled={!!busy || !shown}>
                    {busy === `${what}-clear` ? '…' : clear}
                  </Button>
                )}
              </div>
            </div>
            <p className="text-zinc-500 text-[11px] mt-2">{help}</p>
          </Card>
        );
      })}
    </div>
  );
}
